const contentSelectors = [
  // These are in priority order, ie. higher in the list gets priority
  '#main-heading',
  'h1',
  'h2',
  '.hero__title',
  // Fallbacks: just something "contenty" that is the first element after header, hero, or hero-lander
  'main',
];

/**
 * Initializes the "skip to content" accessibility feature.
 * @see https://www.w3schools.com/accessibility/accessibility_skip_links.php
 * 1. Finds the skip link (#skip-to-content).
 * 2. Iterates through a prioritized list of selectors to find the main content element.
 * 3. Makes the target content focusable via script by setting tabindex="-1".
 * 4. Attaches a click handler to move focus upon activation.
 */
export default class SkipToContent {

  /**
   * @type {HTMLAnchorElement}
   */
  #skipLinkElement;

  /**
   * @type {HTMLElement|undefined} target content element
   */
  #targetContent;

  /**
   * @constructor
   * @param {HTMLAnchorElement} skipLinkElement - Skip content link element to attach to.
   */
  constructor( skipLinkElement ) {
    this.#skipLinkElement = skipLinkElement;
    this.findTargetContent();
    this.#skipLinkElement.addEventListener('click', this.onClick.bind(this));
  }

  /**
   * Called on skipLinkElement is pressed.
   * @param {Event} event - press event
   */
  onClick(event) {
    if( this.#targetContent ) {
      event.preventDefault();
      event.stopPropagation();
      this.#targetContent.focus();
    }
  }

  /**
   * Find target content to focus to when skip link element is pressed.
   */
  findTargetContent() {
    for (let selector of contentSelectors) {
      this.#targetContent = document.querySelector(selector);
      if (this.#targetContent) break;
    }

    // Add tabindex="-1" on the target element to make it programmatically focusable.
    if(this.#targetContent && this.#targetContent.getAttribute('tabindex') === null) {
      this.#targetContent.setAttribute('tabindex', '-1');
    } else {
      console.warn('SkipToContent could not be initialized because target content was not found.')
    }
  }

  /**
   * Initializes the logic.
   */
  static init() {
    const skipLink = document.querySelector('#skip-to-content');

    if( skipLink ) {
      new SkipToContent( skipLink );
    }
  }
}
